import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface ThemeSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Smaller text shown after the label. */
  note?: string;
}

interface ThemeSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: ThemeSelectOption[];
  placeholder?: string;
  /** Extra classes on the trigger button (e.g. error border). */
  className?: string;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  /** Fired when an option is hovered or keyboard-highlighted (empty when cleared). */
  onOptionHighlight?: (value: string) => void;
}

/**
 * Fully styled select — native select popups cannot match dark themes on macOS.
 */
export function ThemeSelect({
  value,
  onChange,
  options,
  placeholder = '',
  className = '',
  disabled = false,
  id,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  onOptionHighlight,
}: ThemeSelectProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const onHighlightRef = useRef(onOptionHighlight);
  onHighlightRef.current = onOptionHighlight;

  const selected = options.find((o) => o.value === value);
  const enabledOptions = options.filter((o) => !o.disabled);

  const emitHighlight = (optionValue: string) => {
    onHighlightRef.current?.(optionValue);
  };

  const clearHighlight = () => {
    onHighlightRef.current?.('');
  };

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setHighlightIndex(-1);
      clearHighlight();
      return;
    }
    const enabled = options.filter((o) => !o.disabled);
    const idx = enabled.findIndex((o) => o.value === value);
    setHighlightIndex(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [open, value]);

  useEffect(() => {
    if (!open || highlightIndex < 0) return;
    const option = enabledOptions[highlightIndex];
    if (option) emitHighlight(option.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from highlightIndex
  }, [highlightIndex, open]);

  const selectOption = (option: ThemeSelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
    clearHighlight();
  };

  const moveHighlight = (delta: number) => {
    if (enabledOptions.length === 0) return;
    setHighlightIndex((prev) => {
      const start = prev < 0 ? (delta > 0 ? -1 : 0) : prev;
      const next = (start + delta + enabledOptions.length) % enabledOptions.length;
      return next;
    });
  };

  const onTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) setOpen(true);
        else moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) setOpen(true);
        else moveHighlight(-1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!open) {
          setOpen(true);
        } else if (highlightIndex >= 0 && enabledOptions[highlightIndex]) {
          selectOption(enabledOptions[highlightIndex]);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        className={`input-field relative w-full text-left pr-10 appearance-none flex items-center ${
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        } ${className}`}
      >
        <span className={`flex min-w-0 items-baseline gap-1.5 ${selected ? '' : 'opacity-50'}`}>
          <span className="truncate">{selected?.label ?? placeholder}</span>
          {selected?.note ? (
            <span className="shrink-0 text-xs text-gray-500">{selected.note}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-activedescendant={
            highlightIndex >= 0 && enabledOptions[highlightIndex]
              ? `${listId}-opt-${enabledOptions[highlightIndex].value}`
              : undefined
          }
          className="theme-select-menu absolute z-50 mt-1 max-h-60 w-full overflow-auto border py-1 shadow-lg"
          onMouseLeave={() => clearHighlight()}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            const enabledIdx = enabledOptions.findIndex((o) => o.value === option.value);
            const isHighlighted = !option.disabled && enabledIdx === highlightIndex;

            return (
              <li
                key={option.value || '__placeholder__'}
                id={`${listId}-opt-${option.value}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                className={`theme-select-option flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                  option.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                } ${isSelected ? 'theme-select-option--selected' : ''} ${
                  isHighlighted ? 'theme-select-option--active' : ''
                }`}
                onMouseEnter={() => {
                  if (!option.disabled && enabledIdx >= 0) {
                    setHighlightIndex(enabledIdx);
                    emitHighlight(option.value);
                  }
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectOption(option)}
              >
                <span className="w-4 shrink-0 flex justify-center">
                  {isSelected ? <Check className="w-4 h-4" /> : null}
                </span>
                <span className="truncate">{option.label}</span>
                {option.note ? (
                  <span className="shrink-0 text-xs text-gray-500">{option.note}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
